import moment from 'moment-timezone-all';
import { Moment } from 'moment';
import { getEntitiesData } from 'src/apis/QuerybuilderAPI';
import { SEARCH_OPERATORS_PRIMITIVE } from 'src/constants/SearchOperators';
import { INVOICE_PAYMENT_STATUS, INVOICE_STATE } from 'src/constants/Invoices';
import { FiltersPayload } from 'src/models/AdvanceSearchModel';
import {
  CampaignDonationRow,
  CampaignFeeRow,
  CampaignGoalSummary,
  CampaignInvoiceRow,
  CampaignStatistics,
  CampaignStatisticsInput,
  CampaignStatisticsWarning,
  DonationTimeBucket,
  DonationTimeSeries,
  FeeCoverageStats,
  FeeWeeklyBucket,
  InstallmentPlanLengthStats,
  InstallmentPlanStats,
  InstallmentPlanStatus,
  PlanLengthKey,
  PlanMixSlice,
  TimeGranularity,
} from 'src/models/CampaignStatisticsModel';
import { ENTITIES, ORDER, QuerybuilderModel } from 'src/models/QuerybuilderModel';

const PAGE_SIZE = 1000;
const MAX_PAGES = 10;
const MAX_TIME_BUCKETS = 2000;

const PLAN_LENGTHS: PlanLengthKey[] = [
  'oneTime',
  'threeMonths',
  'sixMonths',
  'twelveMonths',
  'other',
];

const INSTALLMENT_LENGTHS: PlanLengthKey[] = [
  'threeMonths',
  'sixMonths',
  'twelveMonths',
  'other',
];

const CANCELED_INVOICE_STATES: string[] = [
  INVOICE_STATE.canceled,
  INVOICE_STATE.abandoned,
  INVOICE_STATE.draft,
  INVOICE_STATE.template,
];

const DONATION_FIELDS = [
  'event_transactions.id',
  'event_transactions.invoice_id',
  'event_transactions.contact_id',
  'event_transactions.donor_name_displayed',
  'event_transactions.is_anonymous',
  'event_transactions.nbr_installments',
  'event_transactions.installment_amount_invoice_currency',
  'event_transactions.pledge_date',
  'event_transactions.created_at',
  'invoices.total',
  'invoices.payment_status',
  'payments.payment_type',
  'contacts.first_name',
  'contacts.last_name',
  'contacts.company_name',
];

const INVOICE_FIELDS = [
  'invoices.id',
  'invoices.event_id',
  'invoices.contact_id',
  'invoices.total',
  'invoices.subtotal',
  'invoices.paid',
  'invoices.scheduled',
  'invoices.balance',
  'invoices.payment_status',
  'invoices.state',
  'invoices.created_at',
  'invoices.due_date',
  'invoices.contact_first_name',
  'invoices.contact_last_name',
  'invoices.contact_company_name',
];

const FEE_FIELDS = [
  ...INVOICE_FIELDS,
  'orders.id',
  'orders.product_id',
  'orders.quantity',
  'orders.paid',
  'products.id',
  'products.price',
];

export interface EntityRowsResult {
  rows: Array<Record<string, any>>;
  total: number;
  truncated: boolean;
  failed: boolean;
}

export interface CampaignDonationsResult extends EntityRowsResult {
  donations: CampaignDonationRow[];
}

export interface CampaignInvoicesResult extends EntityRowsResult {
  invoices: CampaignInvoiceRow[];
}

export interface CampaignFeeLinesResult extends EntityRowsResult {
  feeRows: CampaignFeeRow[];
  ordersAvailable: boolean;
}

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  const parsed = Number.parseFloat(String(value ?? ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const toNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = toNumber(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const clamp = (value: number, min: number, max: number): number => (
  Math.min(max, Math.max(min, value))
);

const joinName = (firstName: unknown, lastName: unknown, companyName: unknown): string => {
  const personName = [firstName, lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  return personName || String(companyName ?? '').trim();
};

const getPlanLength = (installments: number): PlanLengthKey => {
  switch (installments) {
    case 1:
      return 'oneTime';
    case 3:
      return 'threeMonths';
    case 6:
      return 'sixMonths';
    case 12:
      return 'twelveMonths';
    default:
      return 'other';
  }
};

export const fetchAllPages = async (
  organisation: string,
  entity: ENTITIES,
  query: QuerybuilderModel,
): Promise<EntityRowsResult> => {
  const rows: Array<Record<string, any>> = [];
  let total = 0;
  let failed = false;

  const loadPage = async (page: number): Promise<void> => {
    if (page > MAX_PAGES) return;

    let response: any;
    try {
      response = await getEntitiesData(organisation, entity, {
        ...query,
        pagination: { page, size: PAGE_SIZE },
      });
    } catch {
      failed = true;
      return;
    }

    if (!response.success) {
      failed = true;
      return;
    }

    const batch = (response.data?.result ?? []) as Array<Record<string, any>>;
    total = toNumber(response.data?.count) || batch.length;
    rows.push(...batch);

    if (batch.length >= PAGE_SIZE && rows.length < total) {
      await loadPage(page + 1);
    }
  };

  await loadPage(1);

  return {
    rows,
    total,
    truncated: !failed && rows.length < total,
    failed,
  };
};

const mapDonationRow = (raw: Record<string, any>): CampaignDonationRow => {
  const id = String(raw['event_transactions.id'] ?? '');
  const invoiceId = toNullableNumber(raw['event_transactions.invoice_id']);
  const contactId = toNullableNumber(raw['event_transactions.contact_id']);
  const pledged = toNumber(raw['invoices.total']);
  const collected = toNumber(raw['invoices.paid']);
  const installments = Math.max(
    1,
    Math.round(toNumber(raw['event_transactions.nbr_installments'])) || 1,
  );

  return {
    id,
    invoiceId,
    contactId,
    donorKey: contactId ? `contact:${contactId}` : `invoice:${invoiceId ?? id}`,
    donorName: joinName(
      raw['contacts.first_name'],
      raw['contacts.last_name'],
      raw['contacts.company_name'],
    ) || String(raw['event_transactions.donor_name_displayed'] ?? '').trim(),
    isAnonymous: Boolean(raw['event_transactions.is_anonymous']),
    createdAt: String(
      raw['event_transactions.created_at']
      ?? raw['event_transactions.pledge_date']
      ?? '',
    ),
    paymentType: String(raw['payments.payment_type'] ?? ''),
    installments,
    installmentAmount: toNumber(
      raw['event_transactions.installment_amount_invoice_currency'],
    ),
    pledged,
    subtotal: toNumber(raw['invoices.subtotal']),
    collected,
    scheduled: toNumber(raw['invoices.scheduled'])
      || Math.max(0, pledged - collected),
    balance: toNumber(raw['invoices.balance']),
    status: String(raw['invoices.payment_status'] ?? ''),
    invoiceState: String(raw['invoices.state'] ?? ''),
  };
};

const mapInvoiceRow = (raw: Record<string, any>): CampaignInvoiceRow => ({
  id: toNumber(raw['invoices.id']),
  contactId: toNullableNumber(raw['invoices.contact_id']),
  total: toNumber(raw['invoices.total']),
  subtotal: toNumber(raw['invoices.subtotal']),
  paid: toNumber(raw['invoices.paid']),
  scheduled: toNumber(raw['invoices.scheduled']),
  balance: toNumber(raw['invoices.balance']),
  status: String(raw['invoices.payment_status'] ?? ''),
  state: String(raw['invoices.state'] ?? ''),
  createdAt: String(raw['invoices.created_at'] ?? ''),
  dueDate: String(raw['invoices.due_date'] ?? ''),
});

const mergeDonationsWithInvoices = (
  donations: CampaignDonationRow[],
  invoices: CampaignInvoiceRow[],
): CampaignDonationRow[] => {
  const invoiceById = new Map<number, CampaignInvoiceRow>();
  invoices.forEach((invoice) => invoiceById.set(invoice.id, invoice));

  return donations.map((donation) => {
    if (donation.invoiceId === null) return donation;
    const invoice = invoiceById.get(donation.invoiceId);
    if (!invoice) return donation;

    return {
      ...donation,
      createdAt: donation.createdAt || invoice.createdAt,
      pledged: invoice.total,
      subtotal: invoice.subtotal,
      collected: invoice.paid,
      scheduled: invoice.scheduled || Math.max(0, invoice.total - invoice.paid),
      balance: invoice.balance,
      status: invoice.status || donation.status,
      invoiceState: invoice.state || donation.invoiceState,
    };
  });
};

const mapFeeRows = (raw: Record<string, any>): CampaignFeeRow[] => {
  const hasOrderFields = raw['orders.id'] !== undefined
    || raw['orders.product_id'] !== undefined;

  if (!hasOrderFields) return [];

  const invoiceId = toNumber(raw['invoices.id']);
  const contactId = toNullableNumber(raw['invoices.contact_id']);
  const quantity = toNumber(raw['orders.quantity']);
  const price = toNumber(raw['products.price']);
  const paid = toNumber(raw['orders.paid']);
  const feePledged = quantity > 0 && price > 0 ? quantity * price : paid;

  return [{
    invoiceId,
    orderId: toNullableNumber(raw['orders.id']),
    contactId,
    donorKey: contactId ? `contact:${contactId}` : `invoice:${invoiceId}`,
    donorName: joinName(
      raw['invoices.contact_first_name'],
      raw['invoices.contact_last_name'],
      raw['invoices.contact_company_name'],
    ),
    isAnonymous: false,
    createdAt: String(raw['invoices.created_at'] ?? ''),
    feePledged,
    feePaid: paid || feePledged,
    status: String(raw['invoices.payment_status'] ?? ''),
    installments: 1,
  }];
};

export const fetchCampaignDonations = async (
  organisation: string,
  fundraisingId: string,
): Promise<CampaignDonationsResult> => {
  const result = await fetchAllPages(organisation, ENTITIES.fundraisingTransactions, {
    fields: DONATION_FIELDS,
    filters: {
      and: [[
        'event_transactions.event_id',
        SEARCH_OPERATORS_PRIMITIVE.eq,
        String(fundraisingId),
      ]],
    } as FiltersPayload,
    order: [['event_transactions.created_at', ORDER.asc]],
  });

  return {
    ...result,
    donations: result.rows.map(mapDonationRow),
  };
};

export const fetchCampaignPledgedTotal = async (
  organisation: string,
  fundraisingId: string,
): Promise<number> => {
  const result = await fetchAllPages(organisation, ENTITIES.fundraisingTransactions, {
    fields: ['invoices.total'],
    filters: {
      and: [[
        'event_transactions.event_id',
        SEARCH_OPERATORS_PRIMITIVE.eq,
        String(fundraisingId),
      ]],
    } as FiltersPayload,
    order: [['event_transactions.id', ORDER.asc]],
  });

  if (result.failed) return Number.NaN;

  return result.rows.reduce(
    (sum, row) => sum + toNumber(row['invoices.total']),
    0,
  );
};

export const fetchCampaignInvoices = async (
  organisation: string,
  fundraisingId: string,
): Promise<CampaignInvoicesResult> => {
  const result = await fetchAllPages(organisation, ENTITIES.invoices, {
    fields: INVOICE_FIELDS,
    filters: {
      and: [[
        'invoices.event_id',
        SEARCH_OPERATORS_PRIMITIVE.eq,
        String(fundraisingId),
      ]],
    } as FiltersPayload,
    order: [['invoices.id', ORDER.asc]],
  });

  return {
    ...result,
    invoices: result.rows.map(mapInvoiceRow),
  };
};

export const fetchCampaignFeeLines = async (
  organisation: string,
  fundraisingId: string,
  feeProductId: number,
): Promise<CampaignFeeLinesResult> => {
  const result = await fetchAllPages(organisation, ENTITIES.invoices, {
    fields: FEE_FIELDS,
    filters: {
      and: [
        ['invoices.event_id', SEARCH_OPERATORS_PRIMITIVE.eq, String(fundraisingId)],
        ['orders.product_id', SEARCH_OPERATORS_PRIMITIVE.eq, feeProductId],
      ],
    } as FiltersPayload,
    order: [['invoices.id', ORDER.asc]],
  });

  const seenOrders = new Set<string>();
  const feeRows: CampaignFeeRow[] = [];

  result.rows.forEach((raw) => {
    mapFeeRows(raw).forEach((feeRow) => {
      const uniqueKey = `${feeRow.invoiceId}:${feeRow.orderId ?? feeRow.donorKey}`;
      if (seenOrders.has(uniqueKey)) return;
      seenOrders.add(uniqueKey);
      feeRows.push(feeRow);
    });
  });

  return {
    ...result,
    feeRows,
    ordersAvailable: feeRows.length > 0 || (result.total === 0 && !result.failed),
  };
};

const getBucket = (value: string, granularity: TimeGranularity, timeZone: string): Moment => {
  const date = moment.utc(value).tz(timeZone);
  return granularity === 'weekly' ? date.startOf('isoWeek') : date.startOf('day');
};

export const buildDonationTimeSeries = (
  donations: CampaignDonationRow[],
  granularity: TimeGranularity,
  timeZone: string,
): DonationTimeSeries => {
  const bucketsByKey = new Map<string, DonationTimeBucket>();
  const startsByKey = new Map<string, Moment>();

  donations.forEach((donation) => {
    if (!donation.createdAt) return;
    const parsed = moment.utc(donation.createdAt);
    if (!parsed.isValid()) return;

    const bucketStart = getBucket(donation.createdAt, granularity, timeZone);
    const key = bucketStart.format('YYYY-MM-DD');
    const isInstallment = donation.installments > 1;
    const current = bucketsByKey.get(key) ?? {
      key,
      start: bucketStart.toISOString(),
      label: bucketStart.format('MMM D'),
      oneTimeCount: 0,
      installmentCount: 0,
      oneTimeAmount: 0,
      installmentAmount: 0,
      runningCount: 0,
      runningAmount: 0,
    };

    current.oneTimeCount += isInstallment ? 0 : 1;
    current.installmentCount += isInstallment ? 1 : 0;
    current.oneTimeAmount += isInstallment ? 0 : donation.pledged;
    current.installmentAmount += isInstallment ? donation.pledged : 0;
    bucketsByKey.set(key, current);
    startsByKey.set(key, bucketStart);
  });

  if (!startsByKey.size) {
    return { buckets: [], truncated: false };
  }

  const sortedKeys = Array.from(startsByKey.keys()).sort();
  const firstStart = startsByKey.get(sortedKeys[0]) as Moment;
  const lastStart = startsByKey.get(sortedKeys[sortedKeys.length - 1]) as Moment;
  const showYear = firstStart.year() !== lastStart.year();

  const buckets: DonationTimeBucket[] = [];
  let cursor = firstStart.clone();
  let truncated = false;
  let runningCount = 0;
  let runningAmount = 0;

  while (cursor.isSameOrBefore(lastStart)) {
    if (buckets.length >= MAX_TIME_BUCKETS) {
      truncated = true;
      break;
    }

    const key = cursor.format('YYYY-MM-DD');
    const bucket = bucketsByKey.get(key) ?? {
      key,
      start: cursor.toISOString(),
      label: '',
      oneTimeCount: 0,
      installmentCount: 0,
      oneTimeAmount: 0,
      installmentAmount: 0,
      runningCount: 0,
      runningAmount: 0,
    };

    runningCount += bucket.oneTimeCount + bucket.installmentCount;
    runningAmount += bucket.oneTimeAmount + bucket.installmentAmount;
    buckets.push({
      ...bucket,
      start: cursor.toISOString(),
      label: cursor.format(showYear ? 'MMM D, YYYY' : 'MMM D'),
      runningCount,
      runningAmount,
    });
    cursor = granularity === 'weekly' ? cursor.add(1, 'week') : cursor.add(1, 'day');
  }

  return { buckets, truncated };
};

export const buildPlanMix = (donations: CampaignDonationRow[]): PlanMixSlice[] => {
  const mix: Record<PlanLengthKey, PlanMixSlice> = {
    oneTime: { key: 'oneTime', count: 0, amount: 0 },
    threeMonths: { key: 'threeMonths', count: 0, amount: 0 },
    sixMonths: { key: 'sixMonths', count: 0, amount: 0 },
    twelveMonths: { key: 'twelveMonths', count: 0, amount: 0 },
    other: { key: 'other', count: 0, amount: 0 },
  };

  donations.forEach((donation) => {
    const slice = mix[getPlanLength(donation.installments)];
    slice.count += 1;
    slice.amount += donation.pledged;
  });

  return PLAN_LENGTHS.map((key) => mix[key]);
};

const isPastDue = (dueDate: string): boolean => {
  if (!dueDate) return false;
  const due = moment.utc(dueDate);
  return due.isValid() && due.isBefore(moment.utc());
};

const classifyInstallmentPlan = (invoice: CampaignInvoiceRow): InstallmentPlanStatus => {
  if (CANCELED_INVOICE_STATES.includes(invoice.state)) {
    return 'canceled';
  }

  switch (invoice.status) {
    case INVOICE_PAYMENT_STATUS.paid:
      return 'completed';
    case INVOICE_PAYMENT_STATUS.scheduled_due:
      return 'pastDue';
    case INVOICE_PAYMENT_STATUS.scheduled:
      return 'active';
    case INVOICE_PAYMENT_STATUS.not_paid:
    case INVOICE_PAYMENT_STATUS.partially_paid:
      return isPastDue(invoice.dueDate) ? 'pastDue' : 'active';
    default:
      return 'other';
  }
};

const invoiceFromDonation = (donation: CampaignDonationRow): CampaignInvoiceRow => ({
  id: donation.invoiceId ?? 0,
  contactId: donation.contactId,
  total: donation.pledged,
  subtotal: donation.subtotal,
  paid: donation.collected,
  scheduled: donation.scheduled,
  balance: donation.balance,
  status: donation.status,
  state: donation.invoiceState,
  createdAt: donation.createdAt,
  dueDate: '',
});

export const buildInstallmentPlans = (
  donations: CampaignDonationRow[],
  invoices: CampaignInvoiceRow[],
): InstallmentPlanStats => {
  const invoiceById = new Map<number, CampaignInvoiceRow>();
  invoices.forEach((invoice) => invoiceById.set(invoice.id, invoice));

  const byLength: Record<PlanLengthKey, InstallmentPlanLengthStats> = {
    oneTime: {
      key: 'oneTime', planCount: 0, collected: 0, scheduled: 0,
    },
    threeMonths: {
      key: 'threeMonths', planCount: 0, collected: 0, scheduled: 0,
    },
    sixMonths: {
      key: 'sixMonths', planCount: 0, collected: 0, scheduled: 0,
    },
    twelveMonths: {
      key: 'twelveMonths', planCount: 0, collected: 0, scheduled: 0,
    },
    other: {
      key: 'other', planCount: 0, collected: 0, scheduled: 0,
    },
  };

  let activeCount = 0;
  let completedCount = 0;
  let pastDueCount = 0;
  let canceledCount = 0;
  let expectedMonthlyAmount = 0;

  donations.forEach((donation) => {
    if (donation.installments <= 1) return;

    const invoice = (donation.invoiceId !== null
      ? invoiceById.get(donation.invoiceId)
      : undefined) ?? invoiceFromDonation(donation);
    const status = classifyInstallmentPlan(invoice);

    if (status === 'canceled') {
      canceledCount += 1;
      return;
    }

    if (status === 'active') {
      activeCount += 1;
      expectedMonthlyAmount += donation.installmentAmount > 0
        ? donation.installmentAmount
        : invoice.total / donation.installments;
    } else if (status === 'completed') {
      completedCount += 1;
    } else if (status === 'pastDue') {
      pastDueCount += 1;
    }

    const lengthStats = byLength[getPlanLength(donation.installments)];
    lengthStats.planCount += 1;
    lengthStats.collected += invoice.paid;
    lengthStats.scheduled += invoice.scheduled;
  });

  return {
    activeCount,
    completedCount,
    pastDueCount,
    canceledCount,
    expectedMonthlyAmount,
    byLength: INSTALLMENT_LENGTHS.map((key) => byLength[key]),
  };
};

const buildFeeWeekly = (
  feeRows: CampaignFeeRow[],
  timeZone: string,
): FeeWeeklyBucket[] => {
  const grouped = new Map<string, { start: Moment; amount: number }>();

  feeRows.forEach((feeRow) => {
    if (!feeRow.createdAt) return;
    const parsed = moment.utc(feeRow.createdAt);
    if (!parsed.isValid()) return;

    const start = moment.utc(feeRow.createdAt).tz(timeZone).startOf('isoWeek');
    const key = start.format('YYYY-MM-DD');
    const current = grouped.get(key) ?? { start, amount: 0 };
    current.amount += feeRow.feePaid;
    grouped.set(key, current);
  });

  if (!grouped.size) return [];

  const sortedKeys = Array.from(grouped.keys()).sort();
  const firstStart = (grouped.get(sortedKeys[0]) as { start: Moment }).start;
  const lastStart = (grouped.get(sortedKeys[sortedKeys.length - 1]) as { start: Moment }).start;
  const showYear = firstStart.year() !== lastStart.year();

  const buckets: FeeWeeklyBucket[] = [];
  let cursor = firstStart.clone();

  while (cursor.isSameOrBefore(lastStart) && buckets.length < MAX_TIME_BUCKETS) {
    const key = cursor.format('YYYY-MM-DD');
    buckets.push({
      key,
      start: cursor.toISOString(),
      label: cursor.format(showYear ? 'MMM D, YYYY' : 'MMM D'),
      amount: grouped.get(key)?.amount ?? 0,
    });
    cursor = cursor.add(1, 'week');
  }

  return buckets;
};

export const buildFeeCoverage = (
  donations: CampaignDonationRow[],
  invoices: CampaignInvoiceRow[],
  feeRows: CampaignFeeRow[],
  feeEnabled: boolean,
  feeDataAvailable: boolean,
  adminFeePercent: string | number,
  timeZone: string,
): FeeCoverageStats => {
  const feeRate = clamp(toNumber(adminFeePercent) / 100, 0, 1);

  if (!feeEnabled) {
    return {
      isEnabled: false,
      dataAvailable: false,
      feeRate,
      eligibleSubtotal: 0,
      feeDue: 0,
      donorCovered: 0,
      charityPaid: 0,
      shareCovered: 0,
      optInRate: 0,
      donorsWhoCovered: 0,
      weekly: [],
      rows: [],
    };
  }

  if (!feeDataAvailable) {
    return {
      isEnabled: true,
      dataAvailable: false,
      feeRate,
      eligibleSubtotal: 0,
      feeDue: 0,
      donorCovered: 0,
      charityPaid: 0,
      shareCovered: 0,
      optInRate: 0,
      donorsWhoCovered: 0,
      weekly: [],
      rows: [],
    };
  }

  const giftInvoiceIds = new Set<number>();
  donations.forEach((donation) => {
    if (donation.invoiceId !== null) giftInvoiceIds.add(donation.invoiceId);
  });

  const giftSubtotal = invoices.length
    ? invoices
      .filter((invoice) => giftInvoiceIds.has(invoice.id))
      .reduce((sum, invoice) => sum + invoice.subtotal, 0)
    : donations.reduce((sum, donation) => sum + donation.subtotal, 0);

  const feePledged = feeRows.reduce((sum, feeRow) => sum + feeRow.feePledged, 0);
  const eligibleSubtotal = Math.max(0, giftSubtotal - feePledged);
  const feeDue = eligibleSubtotal * feeRate;
  const donorCovered = feeRows.reduce((sum, feeRow) => sum + feeRow.feePaid, 0);
  const charityPaid = Math.max(0, feeDue - donorCovered);
  const shareCovered = feeDue > 0 ? Math.min(1, donorCovered / feeDue) : 0;

  const allDonorKeys = new Set<string>();
  donations.forEach((donation) => allDonorKeys.add(donation.donorKey));
  const feeDonorKeys = new Set<string>();
  feeRows.forEach((feeRow) => feeDonorKeys.add(feeRow.donorKey));
  const optInRate = allDonorKeys.size > 0
    ? clamp(feeDonorKeys.size / allDonorKeys.size, 0, 1)
    : 0;

  const donationByInvoice = new Map<number, CampaignDonationRow>();
  donations.forEach((donation) => {
    if (donation.invoiceId !== null && !donationByInvoice.has(donation.invoiceId)) {
      donationByInvoice.set(donation.invoiceId, donation);
    }
  });

  const rows = feeRows
    .map((feeRow) => {
      const donation = donationByInvoice.get(feeRow.invoiceId);
      return {
        ...feeRow,
        contactId: feeRow.contactId ?? donation?.contactId ?? null,
        donorName: feeRow.donorName || donation?.donorName || '',
        isAnonymous: donation?.isAnonymous ?? feeRow.isAnonymous,
        installments: donation?.installments ?? feeRow.installments,
      };
    })
    .sort((first, second) => second.createdAt.localeCompare(first.createdAt));

  return {
    isEnabled: true,
    dataAvailable: true,
    feeRate,
    eligibleSubtotal,
    feeDue,
    donorCovered,
    charityPaid,
    shareCovered,
    optInRate,
    donorsWhoCovered: feeDonorKeys.size,
    weekly: buildFeeWeekly(feeRows, timeZone),
    rows,
  };
};

export const buildGoalSummary = (
  donations: CampaignDonationRow[],
  feeRows: CampaignFeeRow[],
  feeCoverage: FeeCoverageStats,
  goal: number,
): CampaignGoalSummary => {
  const feePaidByInvoice = new Map<number, number>();
  feeRows.forEach((feeRow) => {
    const current = feePaidByInvoice.get(feeRow.invoiceId) ?? 0;
    feePaidByInvoice.set(feeRow.invoiceId, current + feeRow.feePaid);
  });

  const donorKeys = new Set<string>();
  let oneTimePledged = 0;
  let installmentsCollected = 0;
  let futureInstallments = 0;
  let totalPledged = 0;
  let installmentGifts = 0;

  donations.forEach((donation) => {
    donorKeys.add(donation.donorKey);
    totalPledged += donation.pledged;

    const feePaid = donation.invoiceId !== null
      ? (feePaidByInvoice.get(donation.invoiceId) ?? 0)
      : 0;
    const collected = Math.max(0, donation.collected - feePaid);

    if (donation.installments <= 1) {
      oneTimePledged += Math.max(0, donation.pledged - feePaid);
    } else {
      installmentsCollected += collected;
      installmentGifts += 1;
      if (donation.status !== INVOICE_PAYMENT_STATUS.paid) {
        futureInstallments += donation.scheduled;
      }
    }
  });

  const gifts = donations.length;
  const hasGoal = goal > 0;

  return {
    goal,
    hasGoal,
    oneTimePledged,
    installmentsCollected,
    feesCovered: feeCoverage.donorCovered,
    futureInstallments,
    totalPledged,
    percentOfGoal: hasGoal ? totalPledged / goal : null,
    kpis: {
      gifts,
      donors: donorKeys.size,
      averageGift: gifts > 0 ? totalPledged / gifts : 0,
      installmentShare: gifts > 0 ? installmentGifts / gifts : 0,
      feeCoveringDonors: feeCoverage.donorsWhoCovered,
    },
  };
};

const createEmptyFeeResult = (): CampaignFeeLinesResult => ({
  rows: [],
  feeRows: [],
  total: 0,
  truncated: false,
  failed: false,
  ordersAvailable: false,
});

export const fetchCampaignStatistics = async (
  input: CampaignStatisticsInput,
): Promise<CampaignStatistics> => {
  const feeProductId = toNumber(input.feeProductId);
  const feeEnabled = input.askForFee && feeProductId > 0;

  const [donationsResult, invoicesResult, feeResult] = await Promise.all([
    fetchCampaignDonations(input.organisation, input.fundraisingId),
    fetchCampaignInvoices(input.organisation, input.fundraisingId),
    feeEnabled
      ? fetchCampaignFeeLines(input.organisation, input.fundraisingId, feeProductId)
      : Promise.resolve(createEmptyFeeResult()),
  ]);

  const donations = mergeDonationsWithInvoices(
    donationsResult.donations,
    invoicesResult.invoices,
  );
  const feeDataAvailable = feeEnabled && !feeResult.failed && feeResult.ordersAvailable;
  const feeCoverage = buildFeeCoverage(
    donations,
    invoicesResult.invoices,
    feeResult.feeRows,
    feeEnabled,
    feeDataAvailable,
    input.adminFeePercent,
    input.timeZone,
  );
  const summary = buildGoalSummary(
    donations,
    feeResult.feeRows,
    feeCoverage,
    input.goal,
  );
  const dailySeries = buildDonationTimeSeries(
    donations,
    'daily',
    input.timeZone,
  );
  const weeklySeries = buildDonationTimeSeries(
    donations,
    'weekly',
    input.timeZone,
  );

  const warnings: CampaignStatisticsWarning[] = [];
  if (donationsResult.failed) warnings.push('donationsUnavailable');
  if (invoicesResult.failed) warnings.push('invoicesUnavailable');
  if (!feeEnabled) warnings.push('feesDisabled');
  if (feeEnabled && !feeDataAvailable) warnings.push('feesUnavailable');
  if (!summary.hasGoal) warnings.push('noGoal');
  if (
    donationsResult.truncated
    || invoicesResult.truncated
    || feeResult.truncated
    || dailySeries.truncated
    || weeklySeries.truncated
  ) {
    warnings.push('truncated');
  }

  return {
    summary,
    donationsByDay: dailySeries.buckets,
    donationsByWeek: weeklySeries.buckets,
    planMix: buildPlanMix(donations),
    installmentPlans: buildInstallmentPlans(
      donations,
      invoicesResult.invoices,
    ),
    feeCoverage,
    donations,
    warnings,
  };
};
